const base32 = '0123456789bcdefghjkmnpqrstuvwxyz'
const defaultPrecision = 5

interface Coordinates {
  lat: number
  lon: number
}

interface Bounds {
  sw: Coordinates
  ne: Coordinates
}

/**
 * Calculates the bounding box of a geohash.
 * (c) Chris Veness 2014–2019 / MIT Licence
 */
export const calcBounds = (geohash: string): Bounds => {
  if (geohash.length === 0) throw new Error('Invalid geohash')

  geohash = geohash.toLowerCase()

  let evenBit = true
  let latMin = -90,
    latMax = 90
  let lonMin = -180,
    lonMax = 180

  for (let i = 0; i < geohash.length; i++) {
    const chr = geohash.charAt(i)
    const idx = base32.indexOf(chr)
    if (idx === -1) throw new Error('Invalid geohash character')

    for (let n = 4; n >= 0; n--) {
      const bitN = (idx >> n) & 1
      if (evenBit) {
        const lonMid = (lonMin + lonMax) / 2
        if (bitN === 1) {
          lonMin = lonMid
        } else {
          lonMax = lonMid
        }
      } else {
        const latMid = (latMin + latMax) / 2
        if (bitN === 1) {
          latMin = latMid
        } else {
          latMax = latMid
        }
      }
      evenBit = !evenBit
    }
  }

  return {
    sw: { lat: latMin, lon: lonMin },
    ne: { lat: latMax, lon: lonMax },
  }
}

/**
 * Calculates the approximate center latitude and longitude of a geohash.
 */
export const calculateLatLon = (geohash: string): Coordinates => {
  const bounds = calcBounds(geohash)
  const { lat: latMin, lon: lonMin } = bounds.sw
  const { lat: latMax, lon: lonMax } = bounds.ne

  const lat = (latMin + latMax) / 2
  const lon = (lonMin + lonMax) / 2

  const latPrecision = Math.floor(2 - Math.log10(latMax - latMin))
  const lonPrecision = Math.floor(2 - Math.log10(lonMax - lonMin))

  return {
    lat: Number(lat.toFixed(latPrecision)),
    lon: Number(lon.toFixed(lonPrecision)),
  }
}

/**
 * Encodes a latitude and longitude into a geohash of specified precision.
 */
export const calculateGeohash = (
  lat: number,
  lon: number,
  precision: number = defaultPrecision,
): string => {
  if (
    typeof lat !== 'number' ||
    typeof lon !== 'number' ||
    typeof precision !== 'number' ||
    isNaN(lat) ||
    isNaN(lon) ||
    isNaN(precision)
  ) {
    throw new Error('Invalid input for geohash calculation')
  }

  let idx = 0
  let bit = 0
  let evenBit = true
  let geohash = ''

  let latMin = -90,
    latMax = 90
  let lonMin = -180,
    lonMax = 180

  while (geohash.length < precision) {
    if (evenBit) {
      const lonMid = (lonMin + lonMax) / 2
      if (lon >= lonMid) {
        idx = idx * 2 + 1
        lonMin = lonMid
      } else {
        idx = idx * 2
        lonMax = lonMid
      }
    } else {
      const latMid = (latMin + latMax) / 2
      if (lat >= latMid) {
        idx = idx * 2 + 1
        latMin = latMid
      } else {
        idx = idx * 2
        latMax = latMid
      }
    }

    evenBit = !evenBit

    if (++bit === 5) {
      geohash += base32.charAt(idx)
      bit = 0
      idx = 0
    }
  }

  return geohash
}
